import {
  CoverageNeed,
  ProductCoverageName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  isAkaGroupLifeDeathSelected,
  isDeathInsuranceSelected,
} from "Store/akaAdvice/mappers/mapToCartProducts/mapToCartProducts";

import { DonutChart } from "Components/Chart/DonutChart";
import { DonutChartData } from "Components/Chart/DonutChart/donutChartInterface";
import { getChartSize } from "Components/Chart/chartConfig";
import { ChartDivId } from "Components/Chart/chartInterface";
import { VALUE_TYPE } from "Components/NumberValue";
import { getPriceFormatFromValue } from "Formatting/index";
import { useAkaAdviceBaseAmountSelector } from "Store/akaAdvice/akaAdviceSelectors";
import { getPayment } from "Store/akaAdvice/interfaces/getters";
import { sum } from "Utils/arrayUtils";
import { isDefined } from "Utils/functions";
import { useWindowSize } from "Utils/screenSizeUtils";
import { FormikProps } from "formik";
import React from "react";
import { AkaAdvicePriceFormValues } from "../interfaces";

interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
}

export const AkaAdviceChartDeath = ({
  formProps,
  need,
}: Props): React.ReactElement => {
  const windowSize = useWindowSize();
  const baseAmount = useAkaAdviceBaseAmountSelector();
  const effectivePaymentRateAkaGroupLifeDeath = getPayment(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH,
  ).effectivePaymentRate;

  const { recommendedValueAkaGroupLifeDeath, recommendedValueDeath } =
    getRecommendedValues(
      need,
      baseAmount,
      effectivePaymentRateAkaGroupLifeDeath,
    );

  const { selectedValueAkaGroupLifeDeath, selectedValueDeath } =
    getSelectedValues(
      formProps,
      baseAmount,
      effectivePaymentRateAkaGroupLifeDeath,
    );

  const adviceIsBasedOnData = getAdviceIsBasedOnData(
    need,
    recommendedValueAkaGroupLifeDeath + recommendedValueDeath,
  );
  const recommendationData = getRecommendationData(
    selectedValueAkaGroupLifeDeath,
    selectedValueDeath,
  );

  const needReasonsSum = Object.values(need.needReasons).reduce(sum, 0);
  const selectedCenterSum = selectedValueAkaGroupLifeDeath + selectedValueDeath;

  let adjustment = 0;
  if (recommendedValueAkaGroupLifeDeath + recommendedValueDeath > need.need) {
    adjustment =
      recommendedValueAkaGroupLifeDeath + recommendedValueDeath - need.need;
  }

  return (
    <>
      {Object.keys(need.needReasons).length > 0 && (
        <div className={ChartDivId.DEATH_NEED}>
          <DonutChart
            divId={ChartDivId.DEATH_NEED}
            title="Anbefalingen er <br> basert på"
            centerSum={getPriceFormatFromValue(
              needReasonsSum + adjustment,
              VALUE_TYPE.NOK,
            )}
            data={adviceIsBasedOnData}
            size={getChartSize(windowSize)}
          />
        </div>
      )}
      <div className={ChartDivId.DEATH_SELECTED}>
        <DonutChart
          divId={ChartDivId.DEATH_SELECTED}
          title={getSelectedSumTitle(
            selectedCenterSum,
            recommendedValueAkaGroupLifeDeath + recommendedValueDeath,
          )}
          centerSum={getPriceFormatFromValue(selectedCenterSum, VALUE_TYPE.NOK)}
          data={recommendationData}
          size={getChartSize(windowSize)}
        />
      </div>
    </>
  );
};

const getRecommendedValues = (
  need: CoverageNeed,
  baseAmount: number,
  effectivePaymentRateAkaGroupLifeDeath: number,
): {
  recommendedValueAkaGroupLifeDeath: number;
  recommendedValueDeath: number;
} => {
  const recommendedkaGroupLifeDeathGLevel = getPayment(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH,
  ).advice.recommended;
  const recommendedValueAkaGroupLifeDeath = Math.trunc(
    Number(recommendedkaGroupLifeDeathGLevel) *
      baseAmount *
      effectivePaymentRateAkaGroupLifeDeath,
  );
  const recommendedValueDeath = getPayment(need, ProductCoverageName.DEATH)
    .advice.recommended;
  return { recommendedValueAkaGroupLifeDeath, recommendedValueDeath };
};

const getSelectedValues = (
  formProps: FormikProps<AkaAdvicePriceFormValues>,
  baseAmount: number,
  effectivePaymentRateAkaGroupLifeDeath: number,
): {
  selectedValueAkaGroupLifeDeath: number;
  selectedValueDeath: number;
} => {
  const { akaGroupLifeDeathGLevel, deathInsuredSumSlider } = formProps.values;
  const selectedValueAkaGroupLifeDeath = isAkaGroupLifeDeathSelected(
    formProps.values,
  )
    ? Math.trunc(
        Number(akaGroupLifeDeathGLevel ?? 0) *
          baseAmount *
          effectivePaymentRateAkaGroupLifeDeath,
      )
    : 0;
  const selectedValueDeath = isDeathInsuranceSelected(formProps.values)
    ? deathInsuredSumSlider ?? 0
    : 0;
  return { selectedValueAkaGroupLifeDeath, selectedValueDeath };
};

const getAdviceIsBasedOnData = (
  need: CoverageNeed,
  recommendedValue: number,
): DonutChartData => {
  const { DEBT_DEPENDENTS, INCOME_DEPENDENTS, MINOR_CHILDREN } =
    need.needReasons;

  const values = [];
  const labels = [];
  const colors = [];

  if (isDefined(DEBT_DEPENDENTS) && DEBT_DEPENDENTS > 0) {
    values.push(DEBT_DEPENDENTS);
    labels.push("Gjeld");
    colors.push("#C7AA7F");
  }

  if (recommendedValue > need.need) {
    values.push(recommendedValue - need.need);
    labels.push("Justering produktregler");
    colors.push("#D2F5F9");
  }

  if (isDefined(INCOME_DEPENDENTS) && INCOME_DEPENDENTS > 0) {
    values.push(INCOME_DEPENDENTS);
    labels.push("Penger til ektefelle/samboer");
    colors.push("#4F3B22");
  }

  if (isDefined(MINOR_CHILDREN) && MINOR_CHILDREN > 0) {
    values.push(MINOR_CHILDREN);
    labels.push("Utgifter til barn");
    colors.push("#F5E4C7");
  }

  return { values, labels, colors };
};

const getRecommendationData = (
  selectedValueAkaGroupLifeDeath: number,
  selectedValueDeath: number,
): DonutChartData => {
  const values = [];
  const labels = [];
  const colors = [];

  if (selectedValueAkaGroupLifeDeath > 0) {
    values.push(selectedValueAkaGroupLifeDeath);
    labels.push("A+ Gruppeliv livsforsikring");
    colors.push("#0191A5");
  }

  if (selectedValueDeath > 0) {
    values.push(selectedValueDeath);
    labels.push("Individuell livsforsikring");
    colors.push("#D2F5F9");
  }

  return { values, labels, colors };
};

const getSelectedSumTitle = (
  selectedValue: number,
  recommendedValue: number,
): string => {
  if (selectedValue < recommendedValue) {
    return "Du har valgt mindre <br> enn anbefalingen";
  } else if (selectedValue > recommendedValue) {
    return "Du har valgt mer <br> enn anbefalingen";
  }
  return "Du har valgt å <br> følge anbefalingen";
};
